import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import api from '../api';

export const STATUS_LABELS = {
  created:          'Created',
  collected:        'Collected',
  at_depot:         'At Depot',
  out_for_delivery: 'Out for Delivery',
  delivered:        'Delivered',
  failed_delivery:  'Failed Delivery',
  returned_to_depot:'Returned',
  rescheduled:      'Rescheduled',
  cancelled:        'Cancelled',
};

const ORANGE = [249, 115, 22];
const ORANGE_LIGHT = [255, 247, 237];
const GRAY_LIGHT = [249, 250, 251];

// ── CSV ───────────────────────────────────────────────────────────────────────
export function downloadCSV(rows, columns, filename) {
  const header = columns.map(c => `"${c.header}"`).join(',');
  const body = rows.map(row =>
    columns.map(c => {
      const val = c.value(row);
      return `"${String(val ?? '').replace(/"/g, '""')}"`;
    }).join(',')
  ).join('\n');
  const blob = new Blob(['﻿' + header + '\n' + body], { type: 'text/csv;charset=utf-8;' });
  triggerDownload(blob, filename);
}

// ── Excel ─────────────────────────────────────────────────────────────────────
export function downloadExcel(rows, columns, filename, sheetName = 'Export') {
  const wsData = [
    columns.map(c => c.header),
    ...rows.map(row => columns.map(c => c.value(row) ?? '')),
  ];
  const ws = XLSX.utils.aoa_to_sheet(wsData);
  ws['!cols'] = columns.map(c => ({ wch: c.width || 20 }));
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, sheetName);
  XLSX.writeFile(wb, filename);
}

// ── PDF table (landscape A4) ──────────────────────────────────────────────────
export function downloadTablePDF(rows, columns, filename, title, subtitle) {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const pageW = 297;

  // Orange header bar
  doc.setFillColor(...ORANGE);
  doc.rect(0, 0, pageW, 20, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text('EXPRESSWAY COURIER', 14, 13);
  if (subtitle) {
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.text(subtitle, pageW - 14, 16, { align: 'right' });
  }

  // Title below bar
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(30, 30, 30);
  doc.text(title, 14, 28);

  const colStyles = columns.reduce((acc, c, i) => {
    acc[i] = {};
    if (c.align)    acc[i].halign    = c.align;
    if (c.pdfWidth) acc[i].cellWidth = c.pdfWidth;
    return acc;
  }, {});

  autoTable(doc, {
    startY: 32,
    head: [columns.map(c => c.header)],
    body: rows.map(row => columns.map(c => String(c.value(row) ?? ''))),
    styles: { fontSize: 8, cellPadding: 2.5 },
    headStyles: { fillColor: ORANGE, textColor: 255, fontStyle: 'bold' },
    alternateRowStyles: { fillColor: GRAY_LIGHT },
    columnStyles: colStyles,
    margin: { left: 14, right: 14 },
  });

  addFooter(doc, rows.length, pageW);
  doc.save(filename);
}

// ── Report exports: several tables in one file ───────────────────────────────
// A report is { title, subtitle, sections: [{ name, columns, rows, totals }] }.
// `totals` is an optional row object rendered bold after the rows. The same
// description feeds CSV (sections separated by a blank line), Excel (one
// sheet per section) and PDF (one table after another, landscape A4).
const cellText = (c, row) => { const v = c.value(row); return v == null ? '' : v; };

export function downloadSectionsCSV(report, filename) {
  const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const lines = [esc(report.title), report.subtitle ? esc(report.subtitle) : null, ''].filter((l) => l !== null);
  for (const s of report.sections) {
    if (s.name) lines.push(esc(s.name));
    lines.push(s.columns.map((c) => esc(c.header)).join(','));
    for (const r of s.rows) lines.push(s.columns.map((c) => esc(cellText(c, r))).join(','));
    if (s.totals) lines.push(s.columns.map((c) => esc(cellText(c, s.totals))).join(','));
    lines.push('');
  }
  triggerDownload(new Blob(['\ufeff' + lines.join('\n')], { type: 'text/csv;charset=utf-8;' }), filename);
}

export function downloadSectionsExcel(report, filename) {
  const wb = XLSX.utils.book_new();
  const used = new Set();
  report.sections.forEach((s, i) => {
    const aoa = [[report.title], [report.subtitle || ''], [], s.columns.map((c) => c.header),
      ...s.rows.map((r) => s.columns.map((c) => cellText(c, r)))];
    if (s.totals) aoa.push(s.columns.map((c) => cellText(c, s.totals)));
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws['!cols'] = s.columns.map((c) => ({ wch: c.width || 18 }));
    let name = String(s.name || `Sheet ${i + 1}`).replace(/[\\/?*[\]:]/g, ' ').slice(0, 31) || `Sheet ${i + 1}`;
    while (used.has(name)) name = name.slice(0, 28) + ' ' + (i + 1);
    used.add(name);
    XLSX.utils.book_append_sheet(wb, ws, name);
  });
  XLSX.writeFile(wb, filename);
}

export function downloadSectionsPDF(report, filename) {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const pageW = 297, pageH = 210;
  doc.setFillColor(...ORANGE);
  doc.rect(0, 0, pageW, 20, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(12); doc.setFont('helvetica', 'bold');
  doc.text('EXPRESSWAY COURIER', 14, 13);
  if (report.subtitle) { doc.setFontSize(8); doc.setFont('helvetica', 'normal'); doc.text(report.subtitle, pageW - 14, 16, { align: 'right' }); }
  doc.setFontSize(11); doc.setFont('helvetica', 'bold'); doc.setTextColor(30, 30, 30);
  doc.text(report.title, 14, 28);
  let y = 32, total = 0;
  for (const s of report.sections) {
    if (y > pageH - 40) { doc.addPage(); y = 16; }
    if (s.name) { doc.setFontSize(9.5); doc.setFont('helvetica', 'bold'); doc.setTextColor(30, 30, 30); doc.text(s.name, 14, y + 4); y += 7; }
    const body = s.rows.map((r) => s.columns.map((c) => String(cellText(c, r))));
    if (s.totals) body.push(s.columns.map((c) => String(cellText(c, s.totals))));
    const colStyles = s.columns.reduce((acc, c, i) => { acc[i] = {}; if (c.align) acc[i].halign = c.align; if (c.pdfWidth) acc[i].cellWidth = c.pdfWidth; return acc; }, {});
    autoTable(doc, {
      startY: y,
      head: [s.columns.map((c) => c.header)],
      body,
      styles: { fontSize: 8, cellPadding: 2.2 },
      headStyles: { fillColor: ORANGE, textColor: 255, fontStyle: 'bold' },
      alternateRowStyles: { fillColor: GRAY_LIGHT },
      columnStyles: colStyles,
      margin: { left: 14, right: 14 },
      didParseCell: (d) => { if (s.totals && d.section === 'body' && d.row.index === body.length - 1) { d.cell.styles.fontStyle = 'bold'; d.cell.styles.fillColor = ORANGE_LIGHT; } },
    });
    y = doc.lastAutoTable.finalY + 8;
    total += s.rows.length;
  }
  addFooter(doc, total, pageW);
  doc.save(filename);
}

// ── Client Billing PDF (portrait A4) ─────────────────────────────────────────
export function downloadBillingPDF(report) {
  const { client, parcels, summary, from, to } = report;
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageW = 210;

  // ── Header bar
  doc.setFillColor(...ORANGE);
  doc.rect(0, 0, pageW, 22, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('EXPRESSWAY COURIER', 14, 13);
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.text('Courier Management System', 14, 19);

  // ── Title
  doc.setFontSize(15);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(30, 30, 30);
  doc.text('PARCEL ACTIVITY', pageW / 2, 34, { align: 'center' });
  doc.setDrawColor(...ORANGE);
  doc.setLineWidth(0.5);
  doc.line(14, 37, pageW - 14, 37);

  // ── Info boxes
  const boxTop = 41;
  const boxH   = 32;

  // Client box
  doc.setFillColor(...GRAY_LIGHT);
  doc.roundedRect(14, boxTop, 87, boxH, 2, 2, 'F');
  doc.setFontSize(7);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(149, 97, 30);
  doc.text('CLIENT', 18, boxTop + 6);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(20, 20, 20);
  doc.text(client.name || '', 18, boxTop + 13);
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(80, 80, 80);
  let yy = boxTop + 19;
  if (client.phone)   { doc.text(client.phone, 18, yy);   yy += 5; }
  if (client.email)   { doc.text(client.email, 18, yy);   yy += 5; }
  if (client.address) {
    const lines = doc.splitTextToSize(client.address, 78);
    doc.text(lines.slice(0, 2), 18, yy);
  }

  // Period box
  doc.setFillColor(...GRAY_LIGHT);
  doc.roundedRect(109, boxTop, 87, boxH, 2, 2, 'F');
  doc.setFontSize(7);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(149, 97, 30);
  doc.text('BILLING PERIOD', 113, boxTop + 6);

  const fmtDate = d =>
    new Date(d + 'T00:00:00').toLocaleDateString('en-ZA', { day: '2-digit', month: 'long', year: 'numeric' });
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(20, 20, 20);
  doc.text(fmtDate(from), 113, boxTop + 13);
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(80, 80, 80);
  doc.text(`to  ${fmtDate(to)}`, 113, boxTop + 19);
  const refNo = `REF-${from.replace(/-/g, '')}-${String(client.id || '').slice(0, 6).toUpperCase()}`;
  doc.text(`Ref: ${refNo}`, 113, boxTop + 25);

  // ── Parcels table
  autoTable(doc, {
    startY: boxTop + boxH + 8,
    head: [['Tracking #', 'Description', 'Sender', 'Recipient', 'Status', 'Date', 'Price']],
    body: parcels.map(p => [
      p.tracking_number,
      p.description || '',
      p.sender_name || '',
      p.recipient_name || '',
      STATUS_LABELS[p.status] || p.status,
      new Date(p.created_at).toLocaleDateString('en-ZA'),
      p.final_price ? `R${parseFloat(p.final_price).toFixed(2)}` : '—',
    ]),
    styles: { fontSize: 7.5, cellPadding: 2.5 },
    headStyles: { fillColor: ORANGE, textColor: 255, fontStyle: 'bold', fontSize: 8 },
    alternateRowStyles: { fillColor: GRAY_LIGHT },
    columnStyles: {
      0: { cellWidth: 28, fontStyle: 'bold' },
      1: { cellWidth: 36 },
      2: { cellWidth: 26 },
      3: { cellWidth: 26 },
      4: { cellWidth: 22 },
      5: { cellWidth: 18 },
      6: { cellWidth: 18, halign: 'right' },
    },
    margin: { left: 14, right: 14 },
  });

  // ── Summary box
  const fy = doc.lastAutoTable.finalY + 8;
  const sx = pageW - 14 - 80;

  doc.setFillColor(...ORANGE_LIGHT);
  doc.roundedRect(sx, fy, 80, 46, 2, 2, 'F');
  doc.setDrawColor(...ORANGE);
  doc.setLineWidth(0.3);
  doc.roundedRect(sx, fy, 80, 46, 2, 2, 'S');

  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(30, 30, 30);
  doc.text('SUMMARY', sx + 4, fy + 7);

  const summaryRows = [
    ['Total parcels:',       String(summary.total)],
    ['Delivered:',           String(summary.delivered)],
    ['Pending / In transit:',String(summary.pending)],
    ['Failed delivery:',     String(summary.failed)],
    ['Cancelled:',           String(summary.cancelled)],
  ];
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.5);
  summaryRows.forEach(([lbl, val], idx) => {
    doc.setTextColor(80, 80, 80);
    doc.text(lbl, sx + 4, fy + 13 + idx * 4.5);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(30, 30, 30);
    doc.text(val, sx + 76, fy + 13 + idx * 4.5, { align: 'right' });
    doc.setFont('helvetica', 'normal');
  });

  // Total value band
  doc.setFillColor(...ORANGE);
  doc.rect(sx, fy + 38, 80, 8, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.text('TOTAL VALUE (excl. cancelled)', sx + 4, fy + 43.5);
  doc.text(`R${parseFloat(summary.total_value).toFixed(2)}`, sx + 76, fy + 43.5, { align: 'right' });

  // ── Footer on every page
  addFooter(doc, parcels.length, pageW);

  const safeName = (client.name || 'client').replace(/[^a-z0-9]/gi, '_');
  doc.save(`parcel_activity_${safeName}_${from}_to_${to}.pdf`);
}

// ── Client-facing documents: tax invoice + credit note ───────────────────────
// invoice: /billing/invoices/:id response (header + lines + payments)
// settings: /settings/billing response (company details, VAT no, banking, …)
//
// House style (shared with the server-side statements): the logo top-left
// carries the colour; everything else is ink on white, with the logo's navy for
// table heads and totals. Nothing orange — that was the old template.
const NAVY  = [0, 48, 112];
const INK   = [20, 20, 20];
const GREY  = [80, 80, 80];
const MUTED = [140, 140, 140];
const LINE  = [156, 163, 175];
const FILL  = [243, 244, 246];
const L = 14, R = 196, COL_W = 87, COL2_X = 109; // A4 portrait, 14mm margins

// The company logo, fetched once per version and reused for every PDF. A
// missing or unreadable logo must never stop an invoice from printing.
const logoCache = { version: undefined, logo: null };
async function loadLogo(settings = {}) {
  const version = settings.logo_version ?? 0;
  if (logoCache.version === version) return logoCache.logo;
  try {
    const { data: blob } = await api.get('/settings/billing/logo', { responseType: 'blob' });
    const dataUrl = await new Promise((resolve, reject) => {
      const fr = new FileReader();
      fr.onload = () => resolve(fr.result);
      fr.onerror = () => reject(fr.error);
      fr.readAsDataURL(blob);
    });
    const img = await new Promise((resolve, reject) => {
      const i = new Image();
      i.onload = () => resolve(i);
      i.onerror = () => reject(new Error('logo did not decode'));
      i.src = dataUrl;
    });
    logoCache.logo = { dataUrl, format: blob.type === 'image/jpeg' ? 'JPEG' : 'PNG', w: img.naturalWidth, h: img.naturalHeight };
    logoCache.version = version;
  } catch {
    return null; // not cached, so the next PDF tries again
  }
  return logoCache.logo;
}

const fmtRand = (v) => `R${parseFloat(v || 0).toFixed(2)}`;
const fmtLongDate = (d) => d ? new Date(d).toLocaleDateString('en-ZA', { day: '2-digit', month: 'long', year: 'numeric' }) : '';
const addressLines = (settings) =>
  String(settings.billing_company_address || '').split(/\r?\n/).map((t) => t.trim()).filter(Boolean);

// Logo top-left; document title, number and the date lines top-right.
// Returns the y where the two columns underneath start.
function drawDocHeader(doc, logo, title, number, rightLines) {
  const LOGO_W = 55;
  let logoBottom = 12;
  if (logo) {
    const h = LOGO_W * (logo.h / logo.w);
    try {
      doc.addImage(logo.dataUrl, logo.format, L, 12, LOGO_W, h);
      logoBottom = 12 + h;
    } catch { /* draw without it */ }
  }
  doc.setTextColor(...NAVY);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(22);
  doc.text(title, R, 22, { align: 'right' });
  doc.setFontSize(11);
  doc.setTextColor(...INK);
  doc.text(String(number || ''), R, 29, { align: 'right' });
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(...GREY);
  let ry = 35;
  (rightLines || []).forEach(([lbl, val]) => {
    if (!val) return;
    doc.text(`${lbl} ${val}`, R, ry, { align: 'right' });
    ry += 4.2;
  });
  return Math.max(logoBottom, ry) + 6;
}

// Supplier block: name / registration / VAT boxed, address and contact
// details under it — the supplier side SARS requires on a tax invoice.
function drawCompanyBlock(doc, settings, x, y, w) {
  const lines = [
    [settings.billing_company_name || 'Expressway Couriers (Pty) Ltd', true, 10],
    settings.billing_company_reg ? [`Registration number: ${settings.billing_company_reg}`, false, 8] : null,
    settings.billing_vat_number ? [`VAT number: ${settings.billing_vat_number}`, false, 8] : null,
  ].filter(Boolean);
  const boxH = 4 + lines.length * 4.8 + 1.5;
  doc.setDrawColor(...LINE);
  doc.setLineWidth(0.3);
  doc.rect(x, y, w, boxH, 'S');
  let ly = y + 6;
  lines.forEach(([t, bold, size]) => {
    doc.setFont('helvetica', bold ? 'bold' : 'normal');
    doc.setFontSize(size);
    doc.setTextColor(...INK);
    doc.text(doc.splitTextToSize(t, w - 6)[0] || '', x + 3, ly);
    ly += 4.8;
  });

  let ay = y + boxH + 5.5;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(...GREY);
  const contact = [settings.billing_phone ? `Tel ${settings.billing_phone}` : null, settings.billing_email || null]
    .filter(Boolean).join('   ·   ');
  [...addressLines(settings), contact].filter(Boolean).forEach((t) => {
    doc.text(t, x, ay);
    ay += 4;
  });
  return ay;
}

// The client's side: who the document is addressed to.
function drawPartyBox(doc, label, party, x, y, w) {
  const h = 34;
  doc.setFillColor(...FILL);
  doc.roundedRect(x, y, w, h, 1.5, 1.5, 'F');
  doc.setFontSize(7);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...NAVY);
  doc.text(label, x + 4, y + 6);
  doc.setFontSize(10);
  doc.setTextColor(...INK);
  doc.text(doc.splitTextToSize(party.name || '', w - 8)[0] || '', x + 4, y + 13);
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...GREY);
  let yy = y + 19;
  if (party.vat_number) { doc.text(`VAT No: ${party.vat_number}`, x + 4, yy); yy += 4.5; }
  if (party.phone)      { doc.text(party.phone, x + 4, yy); yy += 4.5; }
  if (party.address)    doc.text(doc.splitTextToSize(party.address, w - 8).slice(0, 2), x + 4, yy);
  return y + h;
}

function drawSmallLabel(doc, text, x, y) {
  doc.setFontSize(7);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...NAVY);
  doc.text(text, x, y);
}

// Closing lines: VAT note + where to send queries. Nothing hardcoded — it all
// comes from Settings → Billing.
function drawQueriesLine(doc, settings, y, note) {
  const addr = addressLines(settings).join(', ');
  const queries = [settings.billing_phone ? `Queries: ${settings.billing_phone}` : null, addr || null].filter(Boolean).join(' · ');
  doc.setFontSize(6.5);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...MUTED);
  const oneLine = queries ? `${note}   ${queries}` : note;
  if (!queries || doc.getTextWidth(oneLine) <= R - L) {
    doc.text(oneLine, L, y);
  } else {
    doc.text(note, L, y);
    doc.text(queries, L, y + 4);
  }
}

// Keep a block of the given height on the current page, or start a new one.
// The footer sits at 292mm, so content may run to 287.
function ensureRoom(doc, y, needed) {
  if (y + needed <= 288) return y;
  doc.addPage();
  return 20;
}

// The document the client gets when an invoice is corrected. SARS wants a
// credit note to carry its own number, point at the tax invoice it corrects,
// and show the VAT being credited separately — that's what this prints.
export async function downloadCreditNotePDF(cn, invoice, settings = {}) {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const logo = await loadLogo(settings);

  const top = drawDocHeader(doc, logo, 'CREDIT NOTE', cn.credit_note_number, [
    ['Credit note date:', fmtLongDate(cn.created_at)],
    ['Corrects tax invoice:', invoice.invoice_number],
    ['Invoice dated:', fmtLongDate(invoice.issued_at || invoice.created_at)],
  ]);
  const leftEnd = drawCompanyBlock(doc, settings, L, top, COL_W);
  const rightEnd = drawPartyBox(doc, 'CREDIT TO', {
    name: invoice.client_name, vat_number: invoice.client_vat_number, address: invoice.client_address,
  }, COL2_X, top, COL_W);

  // Reason
  let py = Math.max(leftEnd, rightEnd) + 10;
  drawSmallLabel(doc, 'REASON FOR THIS CREDIT', L, py);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...INK);
  const reasonLines = doc.splitTextToSize(cn.reason || '', 180);
  doc.text(reasonLines, L, py + 6);
  py += 6 + reasonLines.length * 4.5 + 8;

  // Amounts — VAT shown separately, as required
  const bx = COL2_X, bw = COL_W;
  doc.setFillColor(255, 255, 255);
  doc.setDrawColor(...LINE);
  doc.setLineWidth(0.3);
  doc.rect(bx, py, bw, 30, 'FD');
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...GREY);
  doc.text('Amount credited (excl. VAT)', bx + 4, py + 8);
  doc.text(fmtRand(cn.subtotal), bx + bw - 4, py + 8, { align: 'right' });
  doc.text(`VAT @ ${parseFloat(cn.vat_rate).toFixed(0)}%`, bx + 4, py + 15);
  doc.text(fmtRand(cn.vat_amount), bx + bw - 4, py + 15, { align: 'right' });
  doc.setFillColor(...NAVY);
  doc.rect(bx, py + 20, bw, 10, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.text('TOTAL CREDITED', bx + 4, py + 26.5);
  doc.text(fmtRand(cn.total), bx + bw - 4, py + 26.5, { align: 'right' });

  py += 40;
  drawQueriesLine(doc, settings, py,
    'This credit note reduces the amount owing on the tax invoice shown above. All prices include VAT.');

  doc.save(`${cn.credit_note_number}.pdf`);
}

// Download = build + save. The email flow builds the same document and posts
// the bytes to the API, so what the client receives is exactly what staff see.
export async function downloadInvoicePDF(invoice, settings = {}) {
  const doc = await buildInvoicePDF(invoice, settings);
  doc.save(`${invoice.invoice_number || 'draft-invoice'}.pdf`);
}

const fmtLineDiscount = (type, value) => {
  if (!type) return '';
  const v = parseFloat(value || 0);
  return type === 'percent' ? `${v % 1 ? v.toFixed(1) : v.toFixed(0)}%` : `R${v.toFixed(2)}`;
};

export async function buildInvoicePDF(invoice, settings = {}) {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const logo = await loadLogo(settings);
  const isDraft = invoice.status === 'draft';

  const period = invoice.period_from
    ? `${new Date(invoice.period_from).toLocaleDateString('en-ZA')} – ${new Date(invoice.period_to).toLocaleDateString('en-ZA')}`
    : '';
  // An invoice brought over from QuickBooks keeps the number the client knows
  // it by; the QB- reference is what Expressway files it under.
  const fromQb = invoice.source === 'quickbooks';
  const top = drawDocHeader(doc, logo,
    isDraft ? 'DRAFT INVOICE' : 'TAX INVOICE',
    isDraft ? 'Not yet issued' : fromQb && invoice.source_ref ? `Invoice ${invoice.source_ref}` : invoice.invoice_number,
    isDraft ? [['Prepared:', fmtLongDate(invoice.created_at)], ['', 'This is not a tax invoice']] : [
      ['Invoice date:', fmtLongDate(invoice.issued_at || invoice.created_at)],
      ['Due date:',     fmtLongDate(invoice.due_date)],
      ...(fromQb ? [['Reference:', invoice.invoice_number]] : [['Period:', period]]),
      ['Status:',       (invoice.status || '').replace('_', ' ').toUpperCase()],
    ]);
  const leftEnd = drawCompanyBlock(doc, settings, L, top, COL_W);
  const rightEnd = drawPartyBox(doc, 'BILL TO', {
    name: invoice.client_name, vat_number: invoice.client_vat_number,
    phone: invoice.client_phone, address: invoice.client_address,
  }, COL2_X, top, COL_W);

  // Lines: parcels first, then extra items (Fastway labels etc.). When any
  // line carries its own discount the table shows Price · Discount · Amount so
  // the client can see exactly what came off each line.
  const lines = invoice.lines || [];
  const items = invoice.items || [];
  const net = (gross, l) => (l.net_amount != null ? l.net_amount : parseFloat(gross) - parseFloat(l.line_discount_amount || 0));
  const discounted = [...lines, ...items].some((l) => parseFloat(l.line_discount_amount || 0) > 0);
  const docDate = new Date(invoice.issued_at || invoice.created_at).toLocaleDateString('en-ZA');
  // Items only (no parcels — a QuickBooks invoice, or extras sold on their
  // own): a plain Item · Qty · Unit price · Amount table reads better than the
  // parcel columns with blanks in them.
  const itemsOnly = lines.length === 0 && items.length > 0 && !discounted;
  const exclVat = Boolean(invoice.items_excl_vat);
  const rows = itemsOnly ? items.map((it) => {
    const qty = parseFloat(it.quantity);
    return [it.description, Number.isFinite(qty) ? String(qty) : '', fmtRand(it.unit_price), fmtRand(it.amount)];
  }) : lines.length === 0 && items.length === 0 ? [
    // nothing itemised — say what the invoice is for rather than print an empty table
    [invoice.notes || 'Brought forward from QuickBooks', '', '', '', '', fmtRand(invoice.total)],
  ] : [
    ...lines.map((p) => {
      const base = [p.tracking_number, p.description || '', p.sender_name || '', p.recipient_name || '',
        new Date(p.created_at).toLocaleDateString('en-ZA')];
      return discounted
        ? [...base, fmtRand(p.price), fmtLineDiscount(p.line_discount_type, p.line_discount_value), fmtRand(net(p.price, p))]
        : [...base, fmtRand(p.price)];
    }),
    ...items.map((it) => {
      const qty = parseFloat(it.quantity);
      const base = ['Item', qty !== 1 ? `${it.description}  (${qty} × ${fmtRand(it.unit_price)})` : it.description, '', '', docDate];
      return discounted
        ? [...base, fmtRand(it.amount), fmtLineDiscount(it.line_discount_type, it.line_discount_value), fmtRand(net(it.amount, it))]
        : [...base, fmtRand(it.amount)];
    }),
  ];
  autoTable(doc, {
    startY: Math.max(leftEnd, rightEnd) + 8,
    head: [itemsOnly
      ? ['Item', 'Qty', 'Unit price', exclVat ? 'Amount (excl. VAT)' : 'Amount (incl. VAT)']
      : discounted
        ? ['Tracking #', 'Description', 'Sender', 'Recipient', 'Date', 'Price', 'Discount', 'Amount']
        : ['Tracking #', 'Description', 'Sender', 'Recipient', 'Date', 'Amount (incl. VAT)']],
    body: rows,
    styles: { fontSize: 7.5, cellPadding: 2.5, textColor: INK },
    headStyles: { fillColor: NAVY, textColor: 255, fontStyle: 'bold', fontSize: 8 },
    alternateRowStyles: { fillColor: GRAY_LIGHT },
    columnStyles: itemsOnly ? {
      0: { cellWidth: 110 },
      1: { cellWidth: 16, halign: 'right' },
      2: { cellWidth: 28, halign: 'right' },
      3: { cellWidth: 28, halign: 'right', fontStyle: 'bold' },
    } : discounted ? {
      0: { cellWidth: 24, fontStyle: 'bold' },
      1: { cellWidth: 36 },
      2: { cellWidth: 24 },
      3: { cellWidth: 24 },
      4: { cellWidth: 18 },
      5: { cellWidth: 18, halign: 'right' },
      6: { cellWidth: 19, halign: 'right' },
      7: { cellWidth: 19, halign: 'right', fontStyle: 'bold' },
    } : {
      0: { cellWidth: 30, fontStyle: 'bold' },
      1: { cellWidth: 48 },
      2: { cellWidth: 30 },
      3: { cellWidth: 30 },
      4: { cellWidth: 20 },
      5: { cellWidth: 24, halign: 'right' },
    },
    margin: { left: L, right: L },
  });

  // Totals box (VAT carve-out — prices are VAT-inclusive). A discount shows
  // the pre-discount figure and what came off, then VAT on what remains.
  const discountAmt = parseFloat(invoice.discount_amount || 0);
  const totals = [];
  if (discountAmt > 0) {
    totals.push(['Total before discount (incl. VAT):', fmtRand(parseFloat(invoice.total) + discountAmt)]);
    totals.push([`${invoice.discount_note ? `Discount — ${invoice.discount_note}` : 'Discount'}:`, `-${fmtRand(discountAmt)}`]);
  }
  totals.push(['Subtotal (excl. VAT):', fmtRand(invoice.subtotal)]);
  totals.push([`VAT @ ${parseFloat(invoice.vat_rate).toFixed(0)}%:`, fmtRand(invoice.vat_amount)]);
  const boxH = 8 + totals.length * 5.5 + 9;

  let fy = ensureRoom(doc, doc.lastAutoTable.finalY + 6, boxH + 4);
  const sx = R - 80;
  doc.setFillColor(255, 255, 255);
  doc.setDrawColor(...LINE);
  doc.setLineWidth(0.3);
  doc.rect(sx, fy, 80, boxH, 'FD');
  doc.setFontSize(8);
  totals.forEach(([lbl, val], idx) => {
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...GREY);
    doc.text(doc.splitTextToSize(lbl, 52)[0] || '', sx + 4, fy + 8 + idx * 5.5);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...INK);
    doc.text(val, sx + 76, fy + 8 + idx * 5.5, { align: 'right' });
  });
  doc.setFillColor(...NAVY);
  doc.rect(sx, fy + boxH - 9, 80, 9, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text(isDraft ? 'TOTAL (incl. VAT)' : 'TOTAL DUE (incl. VAT)', sx + 4, fy + boxH - 3);
  doc.text(fmtRand(invoice.total), sx + 76, fy + boxH - 3, { align: 'right' });

  // Payments received (if any)
  let py = fy + boxH + 5;
  if ((invoice.payments || []).length > 0) {
    py = ensureRoom(doc, py, 12 + invoice.payments.length * 4.5);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...INK);
    doc.text('Payments received:', L, py);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...GREY);
    invoice.payments.forEach((p) => {
      py += 4.5;
      const how = p.method === 'eft' ? 'EFT (Capitec)' : String(p.method || '').toUpperCase();
      doc.text(`${new Date(p.paid_at).toLocaleDateString('en-ZA')}  ${how}  ${p.reference || ''}`, L, py);
      doc.setFont('helvetica', 'bold');
      doc.text(fmtRand(p.amount), 100, py, { align: 'right' });
      doc.setFont('helvetica', 'normal');
    });
    py += 6;
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...INK);
    doc.text(`Balance outstanding: ${fmtRand(invoice.balance)}`, L, py);
  }

  // Payment reference. Invoice number FIRST — banks truncate the reference
  // field, and the number is what identifies the payment; the client name is
  // the fallback for matching it if the number gets mangled.
  py = Math.max(py + 7, fy + boxH + 10);
  const bankLines = settings.billing_bank_details ? doc.splitTextToSize(settings.billing_bank_details, 120).slice(0, 6) : [];
  // From py: reference box (to py+9) + gap, banking label + lines, closing line
  py = ensureRoom(doc, py, 15 + (bankLines.length ? 4.5 + bankLines.length * 3.5 : 0) + 4);
  const payRef = `${invoice.invoice_number || 'DRAFT'} ${invoice.client_name || ''}`.trim();
  doc.setFillColor(...FILL);
  doc.setDrawColor(...LINE);
  doc.setLineWidth(0.4);
  doc.roundedRect(L, py - 4, 120, 13, 1.5, 1.5, 'FD');
  doc.setFontSize(6.5);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...NAVY);
  doc.text('USE THIS REFERENCE WHEN YOU PAY', L + 3, py);
  doc.setFontSize(9);
  doc.setTextColor(...INK);
  doc.text(payRef, L + 3, py + 5.5);
  py += 15;

  // Banking details + VAT-inclusive note
  if (bankLines.length) {
    drawSmallLabel(doc, 'BANKING DETAILS', L, py);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...GREY);
    doc.setFontSize(7);
    doc.text(bankLines, L, py + 4.5);
    py += 4.5 + bankLines.length * 3.5;
  }
  drawQueriesLine(doc, settings, py + 4,
    exclVat ? 'Line amounts exclude VAT; VAT is shown in the totals.' : 'All prices include VAT.');

  addFooter(doc, lines.length + items.length, 210);
  return doc;
}

// ── Print (opens new window) ──────────────────────────────────────────────────
export function printTable(rows, columns, title, subtitle) {
  const w = window.open('', '_blank');
  if (!w) { alert('Please allow pop-ups to use the print feature'); return; }
  const html = `<!DOCTYPE html>
<html>
<head>
  <title>${title}</title>
  <style>
    *{margin:0;padding:0;box-sizing:border-box}
    body{font-family:Arial,sans-serif;font-size:11px;color:#111}
    .hdr{background:#f97316;color:#fff;padding:12px 20px 10px}
    .hdr h1{font-size:15px;font-weight:bold}
    .hdr p{font-size:10px;opacity:.85;margin-top:2px}
    .meta{padding:8px 20px 6px;font-size:10px;color:#555}
    table{width:calc(100% - 40px);margin:0 20px;border-collapse:collapse}
    th{background:#f97316;color:#fff;padding:5px 7px;text-align:left;font-size:9px;text-transform:uppercase}
    td{padding:4px 7px;border-bottom:1px solid #e5e7eb;font-size:9px}
    tr:nth-child(even) td{background:#f9fafb}
    .ftr{margin-top:10px;padding:6px 20px;font-size:8px;color:#999;border-top:1px solid #e5e7eb}
    @media print{button{display:none}}
  </style>
</head>
<body>
  <div class="hdr">
    <h1>EXPRESSWAY COURIER</h1>
    <p>${title}${subtitle ? '  &bull;  ' + subtitle : ''}</p>
  </div>
  <table>
    <thead><tr>${columns.map(c => `<th>${c.header}</th>`).join('')}</tr></thead>
    <tbody>${rows.map(row =>
      `<tr>${columns.map(c => `<td>${String(c.value(row) ?? '')}</td>`).join('')}</tr>`
    ).join('')}</tbody>
  </table>
  <div class="ftr">Generated: ${new Date().toLocaleString('en-ZA')} &nbsp;&bull;&nbsp; ${rows.length} record${rows.length !== 1 ? 's' : ''}</div>
</body>
</html>`;
  w.document.write(html);
  w.document.close();
  w.focus();
  setTimeout(() => w.print(), 400);
}

// ── internal helpers ──────────────────────────────────────────────────────────
function triggerDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function addFooter(doc, rowCount, pageW) {
  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    const h = doc.internal.pageSize.getHeight();
    doc.setFontSize(7);
    doc.setTextColor(150, 150, 150);
    doc.text(
      `Generated: ${new Date().toLocaleString('en-ZA')}   •   ${rowCount} record${rowCount !== 1 ? 's' : ''}   •   Page ${i} of ${pageCount}`,
      pageW / 2, h - 5, { align: 'center' }
    );
  }
}

// Statement for an outsourced driver paid per parcel (Money → Driver Pay).
// `st` = { driver_name, driver_email, driver_phone, period_from, period_to,
//          rows: [{ handled_at, leg, tracking_number, sender, recipient,
//                   actual_kg, volumetric_kg, chargeable_kg, rate }],
//          total, paid_at?, reference?, rates? }
export async function downloadDriverStatementPDF(st, settings = {}) {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const logo = await loadLogo(settings);
  const period = `${fmtLongDate(st.period_from)} – ${fmtLongDate(st.period_to)}`;
  const top = drawDocHeader(doc, logo, 'DRIVER STATEMENT', st.paid_at ? 'PAID' : 'FOR PAYMENT', [
    ['Period:', period],
    ['Printed:', fmtLongDate(new Date())],
    st.paid_at ? ['Paid on:', fmtLongDate(st.paid_at)] : null,
    st.reference ? ['Reference:', st.reference] : null,
  ].filter(Boolean));

  const leftBottom = drawCompanyBlock(doc, settings, L, top, COL_W);
  const party = { name: st.driver_name, phone: [st.driver_phone, st.driver_email].filter(Boolean).join('  ·  ') };
  const rightBottom = drawPartyBox(doc, 'PAYABLE TO', party, COL2_X, top, COL_W);
  let y = Math.max(leftBottom, rightBottom) + 6;

  if (st.rates) {
    doc.setFontSize(7.5); doc.setFont('helvetica', 'normal'); doc.setTextColor(...GREY);
    const over = st.rates.over_kg ? `; above ${parseFloat(st.rates.over_kg)} kg, R${parseFloat(st.rates.over_rate).toFixed(2)} per kg over` : '';
    doc.text(`Rate: R${parseFloat(st.rates.first_rate).toFixed(2)} up to ${parseFloat(st.rates.first_kg)} kg, then R${parseFloat(st.rates.step_rate).toFixed(2)} per further ${parseFloat(st.rates.step_kg)} kg or part${over}, on the chargeable weight (actual or L×W×H÷5000, whichever is more).`, L, y, { maxWidth: R - L });
    y += 7;
  }

  const legLabel = (l) => (l === 'pickup' ? 'Pickup' : 'Delivery');
  const kg = (v) => (v == null || v === '' ? '' : parseFloat(v).toFixed(1));
  autoTable(doc, {
    startY: y,
    margin: { left: L, right: 210 - R },
    head: [['Date', 'Leg', 'Tracking', 'From', 'To', 'Actual kg', 'Vol. kg', 'Charge kg', 'Amount']],
    body: st.rows.map((r) => [
      r.handled_at ? new Date(r.handled_at).toLocaleDateString('en-ZA') : '',
      legLabel(r.leg),
      (r.manual_note || r.manual_rate != null) ? `${r.tracking_number}\n(${r.manual_note || 'Manual amount'})` : r.tracking_number,
      r.sender || '', r.recipient || '',
      kg(r.actual_kg), kg(r.volumetric_kg), kg(r.chargeable_kg), fmtRand(r.rate),
    ]),
    foot: [[{ content: `${st.rows.length} parcel${st.rows.length === 1 ? '' : 's'}`, colSpan: 7 },
      { content: 'Total', styles: { halign: 'right' } }, { content: fmtRand(st.total), styles: { halign: 'right' } }]],
    styles: { fontSize: 7.5, cellPadding: 1.6, textColor: INK, lineColor: LINE, lineWidth: 0.1 },
    headStyles: { fillColor: NAVY, textColor: 255, fontStyle: 'bold' },
    footStyles: { fillColor: FILL, textColor: INK, fontStyle: 'bold' },
    columnStyles: { 0: { cellWidth: 18 }, 1: { cellWidth: 15 }, 2: { cellWidth: 20 }, 3: { cellWidth: 34 }, 4: { cellWidth: 34 },
      5: { cellWidth: 15, halign: 'right' }, 6: { cellWidth: 14, halign: 'right' }, 7: { cellWidth: 16, halign: 'right' }, 8: { halign: 'right' } },
    alternateRowStyles: { fillColor: [250, 250, 250] },
  });
  y = doc.lastAutoTable.finalY + 8;
  y = ensureRoom(doc, y, 12);
  drawQueriesLine(doc, settings, y, st.paid_at ? 'Paid — keep for your records.' : 'Please check the parcels and amounts and let us know of any difference before payment.');
  const safe = String(st.driver_name || 'driver').replace(/[^A-Za-z0-9]+/g, '-');
  doc.save(`Driver-statement-${safe}-${st.period_from}-to-${st.period_to}.pdf`);
}
